import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GET, POST } from '../../configs/api';
import { api } from '../../utils/constants';

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const { data, error, message } = await GET(api.getNotifications);
      if (error) {
        return rejectWithValue(message || 'Failed to fetch notifications');
      }
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch notifications');
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'notifications/markNotificationRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const { data, error, message } = await POST(`${api.readNotification}/${notificationId}/read`);
      if (error) {
        return rejectWithValue(message || 'Failed to mark notification as read');
      }
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to mark notification as read');
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllNotificationsRead',
  async (_, { rejectWithValue }) => {
    try {
      const { data, error, message } = await POST(api.readAllNotifications);
      if (error) {
        return rejectWithValue(message || 'Failed to mark all notifications as read');
      }
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to mark all notifications as read');
    }
  }
);

export const archiveNotification = createAsyncThunk(
  'notifications/archiveNotification',
  async (notificationId, { rejectWithValue }) => {
    try {
      const { error, message } = await POST(`${api.archiveNotification}/${notificationId}/archive`);
      if (error) {
        return rejectWithValue(message || 'Failed to archive notification');
      }
      return notificationId;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to archive notification');
    }
  }
);

/** @param {{ text: string; broadcast?: boolean; userIds?: string[] }} payload */
export const pushOwnerNotification = createAsyncThunk(
  'notifications/pushOwnerNotification',
  async ({ text, broadcast, userIds }, { dispatch, rejectWithValue }) => {
    try {
      const body = broadcast ? { text, broadcast: true } : { text, userIds };
      const { data, error, message } = await POST(api.pushOwnerNotification, {}, body);
      if (error) {
        return rejectWithValue(message || 'Failed to send announcement');
      }
      dispatch(fetchNotifications());
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to send announcement');
    }
  }
);

const initialState = {
  items: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearNotifications: (state) => {
      state.items = [];
      state.unreadCount = 0;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchNotifications
      .addCase(fetchNotifications.pending, (state) => {
        // Don't set loading to true to avoid UI flicker during polling
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        if (!Array.isArray(action.payload)) return;
        state.items = action.payload;
        state.unreadCount = action.payload.filter((n) => !n.read_at).length;
        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // markNotificationRead (optimistic update)
      .addCase(markNotificationRead.pending, (state, action) => {
        const notificationId = action.meta.arg;
        const notification = state.items.find((n) => n.id === notificationId);
        // Optimistically mark as read on the frontend only
        if (notification && !notification.read_at) {
          notification.read_at = new Date().toISOString();
        }
        state.unreadCount = state.items.filter((n) => !n.read_at).length;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const updatedNotification = action.payload;
        const index = state.items.findIndex((n) => n.id === updatedNotification.id);
        if (index !== -1) {
          // Preserve any extra fields that were added on the frontend
          state.items[index] = {
            ...state.items[index],
            read_at: updatedNotification.read_at || state.items[index].read_at || new Date().toISOString(),
          };
        }
        state.unreadCount = state.items.filter((n) => !n.read_at).length;
      })
      .addCase(markNotificationRead.rejected, (state, action) => {
        const notificationId = action.meta.arg;
        const notification = state.items.find((n) => n.id === notificationId);
        // Revert optimistic change if API call fails
        if (notification) {
          notification.read_at = null;
        }
        state.unreadCount = state.items.filter((n) => !n.read_at).length;
        state.error = action.payload || state.error;
      })
      // markAllNotificationsRead
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.items = state.items.map((n) => ({
          ...n,
          read_at: n.read_at || new Date().toISOString(),
        }));
        state.unreadCount = 0;
      })
      // archiveNotification
      .addCase(archiveNotification.fulfilled, (state, action) => {
        const archivedId = action.payload;
        state.items = state.items.filter((n) => n.id !== archivedId);
        state.unreadCount = state.items.filter((n) => !n.read_at).length;
      });
  },
});

export const { clearNotifications } = notificationsSlice.actions;

// Selectors
export const getNotifications = (state) => state.notifications;
export const getUnreadCount = (state) => state.notifications.unreadCount;

export default notificationsSlice.reducer;
